import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Trash2, Clock, User, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface IncompleteTest {
  id: string;
  candidate_id: string;
  test_id: string;
  status: string;
  started_at: string;
  candidate_name?: string;
  test_name?: string;
}

interface IncompleteTestsManagerProps {
  candidateId?: string;
  onTestDeleted?: () => void;
}

export function IncompleteTestsManager({ candidateId, onTestDeleted }: IncompleteTestsManagerProps) {
  const [incompleteTests, setIncompleteTests] = useState<IncompleteTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchIncompleteTests();
  }, [candidateId]);

  const fetchIncompleteTests = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('test_results')
        .select(`
          id,
          candidate_id,
          test_id,
          status,
          started_at,
          candidates (full_name),
          occupational_tests (name)
        `)
        .eq('status', 'in_progress')
        .order('started_at', { ascending: false });

      // If candidateId is provided, filter by specific candidate
      if (candidateId) {
        query = query.eq('candidate_id', candidateId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedTests = data?.map(test => ({
        ...test,
        candidate_name: test.candidates?.full_name || 'Άγνωστος',
        test_name: test.occupational_tests?.name || 'IPIP Personality Assessment'
      })) || [];

      setIncompleteTests(formattedTests);
    } catch (error: any) {
      console.error('Error fetching incomplete tests:', error);
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία φόρτωσης ανολοκλήρωτων τεστ",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (testId: string) => {
    try {
      setDeleting(testId);
      
      const { error } = await supabase
        .from('test_results')
        .delete()
        .eq('id', testId)
        .eq('status', 'in_progress'); // Extra safety - only delete incomplete tests

      if (error) throw error;

      toast({
        title: "Επιτυχής Διαγραφή",
        description: "Το ανολοκλήρωτο τεστ διαγράφηκε επιτυχώς",
      });

      // Refresh the list
      await fetchIncompleteTests();
      
      // Notify parent component if callback provided
      if (onTestDeleted) {
        onTestDeleted();
      }
    } catch (error: any) {
      console.error('Error deleting test:', error);
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία διαγραφής ανολοκλήρωτου τεστ",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAllIncomplete = async () => {
    try {
      let query = supabase
        .from('test_results')
        .delete()
        .eq('status', 'in_progress');

      // If candidateId is provided, only delete for that candidate
      if (candidateId) {
        query = query.eq('candidate_id', candidateId);
      }

      const { error } = await query;

      if (error) throw error;

      toast({
        title: "Επιτυχής Διαγραφή",
        description: candidateId 
          ? "Όλα τα ανολοκλήρωτα τεστ του υποψηφίου διαγράφηκαν"
          : "Όλα τα ανολοκλήρωτα τεστ διαγράφηκαν",
      });

      // Refresh the list
      await fetchIncompleteTests();
      
      // Notify parent component if callback provided
      if (onTestDeleted) {
        onTestDeleted();
      }
    } catch (error: any) {
      console.error('Error deleting all incomplete tests:', error);
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία διαγραφής ανολοκλήρωτων τεστ",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('el-GR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Φόρτωση ανολοκλήρωτων τεστ...</div>
        </CardContent>
      </Card>
    );
  }

  if (incompleteTests.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Ανολοκλήρωτα Τεστ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">
            Δεν υπάρχουν ανολοκλήρωτα τεστ.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Ανολοκλήρωτα Τεστ ({incompleteTests.length})
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchIncompleteTests}
              className="flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" />
              Ανανέωση
            </Button>
            {incompleteTests.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="flex items-center gap-1">
                    <Trash2 className="w-4 h-4" />
                    Διαγραφή Όλων
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Διαγραφή Όλων των Ανολοκλήρωτων Τεστ</AlertDialogTitle>
                    <AlertDialogDescription>
                      Είστε σίγουροι ότι θέλετε να διαγράψετε όλα τα ανολοκλήρωτα τεστ{candidateId ? ' αυτού του υποψηφίου' : ''}; 
                      Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteAllIncomplete}>
                      Διαγραφή Όλων
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {incompleteTests.map((test) => (
            <div key={test.id} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">
                    {candidateId ? test.test_name : `${test.candidate_name} - ${test.test_name}`}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Ξεκίνησε: {formatDate(test.started_at)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                  Σε εξέλιξη
                </Badge>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={deleting === test.id}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Διαγραφή Ανολοκλήρωτου Τεστ</AlertDialogTitle>
                      <AlertDialogDescription>
                        Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το ανολοκλήρωτο τεστ; 
                        Όλες οι μερικές απαντήσεις θα χαθούν. Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleDeleteTest(test.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Διαγραφή
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}